/**
 * DaGi announcement bar — auto-fades on mobile only; desktop shows all messages.
 * Standalone script (no ES module imports). Loaded globally from scripts.liquid.
 */
class DagiAnnouncementBar extends HTMLElement {
  #current = 0;
  /** @type {number | undefined} */
  #interval;
  /** @type {MediaQueryList | undefined} */
  #mobileQuery;
  /** @type {MediaQueryList | undefined} */
  #reducedMotionQuery;
  #breakpoint = 750;

  #onVisibilityChange = () => {
    if (document.hidden) {
      this.suspend();
      return;
    }
    this.resume();
  };

  #onBreakpointChange = () => {
    this.refresh();
  };

  connectedCallback() {
    this.#breakpoint = parseInt(this.getAttribute('data-mobile-breakpoint') || '750', 10);
    this.#mobileQuery = window.matchMedia(`(max-width: ${this.#breakpoint - 1}px)`);
    this.#reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.addEventListener('mouseenter', () => this.suspend());
    this.addEventListener('mouseleave', () => this.resume());
    document.addEventListener('visibilitychange', this.#onVisibilityChange);
    this.#mobileQuery.addEventListener('change', this.#onBreakpointChange);

    this.querySelector('.slideshow-control--previous')?.addEventListener('click', (event) => {
      event.preventDefault();
      this.previous();
    });

    this.querySelector('.slideshow-control--next')?.addEventListener('click', (event) => {
      event.preventDefault();
      this.next();
    });

    requestAnimationFrame(() => {
      this.refresh();
    });
  }

  disconnectedCallback() {
    this.suspend();
    this.#mobileQuery?.removeEventListener('change', this.#onBreakpointChange);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
  }

  /** Re-apply mobile/desktop mode (Theme Editor section reloads). */
  refresh() {
    this.#applyMode();
  }

  get #slides() {
    return this.querySelectorAll('.dagi-announcement-bar__slide');
  }

  get autoplayInterval() {
    if (this.getAttribute('data-autoplay-enabled') === 'false') return undefined;

    const value = parseInt(this.getAttribute('autoplay') || '', 10);
    return Number.isNaN(value) ? undefined : value * 1000;
  }

  get #isMobile() {
    return this.#mobileQuery?.matches ?? false;
  }

  next() {
    if (!this.#slides.length) return;
    this.current = this.#current + 1;
  }

  previous() {
    if (!this.#slides.length) return;
    this.current = this.#current - 1;
  }

  /**
   * @param {number} [interval]
   */
  play(interval = this.autoplayInterval) {
    if (!this.#shouldAutoplay() || !interval) return;

    this.suspend();
    this.#interval = window.setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;
      this.next();
    }, interval);
  }

  suspend() {
    if (this.#interval !== undefined) {
      clearInterval(this.#interval);
      this.#interval = undefined;
    }
  }

  resume() {
    if (!this.#shouldAutoplay()) return;
    this.play();
  }

  #shouldAutoplay() {
    if (!this.#isMobile) return false;
    if (this.#reducedMotionQuery?.matches) return false;
    if (!this.autoplayInterval) return false;
    if (this.#slides.length < 2) return false;
    return true;
  }

  #showAllSlides() {
    this.#slides.forEach((slide) => {
      slide.setAttribute('aria-hidden', 'false');
    });
  }

  #applyMode() {
    this.suspend();

    if (this.#isMobile) {
      this.setAttribute('data-dagi-mode', 'mobile');

      if (this.#shouldAutoplay()) {
        this.current = 0;
        this.play();
      } else if (this.#slides.length > 0) {
        this.current = 0;
      }
      return;
    }

    this.setAttribute('data-dagi-mode', 'desktop');
    this.#showAllSlides();
  }

  set current(current) {
    this.#current = current;
    const slides = this.#slides;
    if (!slides.length) return;

    let index = current % slides.length;
    if (index < 0) index += slides.length;

    slides.forEach((slide, slideIndex) => {
      slide.setAttribute('aria-hidden', `${slideIndex !== index}`);
    });
  }
}

if (!customElements.get('dagi-announcement-bar-component')) {
  customElements.define('dagi-announcement-bar-component', DagiAnnouncementBar);
}

document.addEventListener('shopify:section:load', (event) => {
  const root = event.target;
  if (!(root instanceof HTMLElement)) return;

  root.querySelectorAll('dagi-announcement-bar-component').forEach((element) => {
    if (element instanceof DagiAnnouncementBar) {
      requestAnimationFrame(() => element.refresh());
    }
  });
});

document.addEventListener('shopify:section:reorder', (event) => {
  const root = event.target;
  if (!(root instanceof HTMLElement)) return;

  root.querySelectorAll('dagi-announcement-bar-component').forEach((element) => {
    if (element instanceof DagiAnnouncementBar) {
      requestAnimationFrame(() => element.refresh());
    }
  });
});
